import { InputGroup, Spinner } from '@blueprintjs/core';
import styled from '@emotion/styled';
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import internal from 'assert';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import React, { useCallback } from 'react';
import { TLCode } from '../../common/enums';
import { LocaleStore, MunicipalityStore, ProtectionStore, TransferRequestStore, PharmacyStore } from '../../store';
import { Municipality } from '../../store/models/Municipality';
import BaseGrid from '../common/grid/BaseGrid';
import EditCell from '../common/grid/EditCell';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import {
  renderGridDate
} from '../../store/utils';
import moment from 'moment';

import {
  Column,
  ColumnChooser,
  DataGrid,
  Button as DGButton,
  DataGridTypes,
  Export,
  FilterPanel,
  FilterRow,
  GroupPanel,
  Grouping,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
  Sorting,
} from 'devextreme-react/data-grid';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import DataSource from 'devextreme/data/data_source';
import { ProtectionDialog } from './dialog/ProtectionDialog';


interface RequestListProps extends WithTranslateProps {
  PharmacyStore?: Instance<typeof PharmacyStore>;
  ProtectionStore?: Instance<typeof ProtectionStore>;
  TransferRequestStore?: Instance<typeof TransferRequestStore>;
  MunicipalityStore?: Instance<typeof MunicipalityStore>;
  LocaleStore?: Instance<typeof LocaleStore>;
}

interface RequestListState {
  isLoading: boolean;
  showProtectionDialog: boolean;
  currentTransfer?: any;
  municipalityToEdit?: Instance<typeof Municipality>;
  filter: string;
}

interface TransferRequestToDisplay {
  id: number;
  // pharmacyId: number;
  apbNumber: number;
  requestDate: string;
  decisionDate: string;
  decision: string;
  firstPublishDate: string;
  firstPublishDateAfterDecision: string;
  initialAddress: string,
  initialZipCode: string,
  initialCity: string,
  newAddress: string,
  newZipCode: string,
  newCity: string,
  procedure: string;
  chamber: string;
  startDate: string;
  endDate: string;
  duration: string;
  //durationUom: string;
  range: string;
  //rangeUom: string;
}

@inject('ProtectionStore', 'TransferRequestStore', 'PharmacyStore', 'LocaleStore')
@observer
class RequestList extends React.Component<RequestListProps, RequestListState> {
  state = {
    isLoading: false,
    showProtectionDialog: false,
    municipalityToEdit: undefined,
    filter: '',
  } as RequestListState;

  async componentDidMount() {
    const { ProtectionStore, TransferRequestStore } = this.props;
    this.setState({ isLoading: true });
    // await ProtectionStore!.checkListLoaded(); // loadList();
    await TransferRequestStore!.checkListLoaded();
    this.setState({ isLoading: false });
  }

  buildGridData = () => {
    return this.props.TransferRequestStore!.dataList.map((m) => {
      // var ph = this.props.PharmacyStore!.getById(m.pharmacyId!);
      return {
        id: m.transferRequestId,
        apbNumber: m.apbNumber,
        apbNumberToClose: m.apbNumberToClose,
        requestDate: m.requestDate,
        decisionDate: m.decisionDate.startsWith("0001")?'':m.decisionDate,
        decision: m.decision=='+'?'accepté':(m.decision=='-'?'rejeté':''),
        firstPublishDate: m.firstPublishDate,
        firstPublishDateAfterDecision: m.firstPublishDateAfterDecision,
        initialAddress: m.initialAddress,
        initialZipCode: m.initialZipCode,
        initialCity: m.initialCity,
        newAddress: m.newAddress,
        newZipCode: m.newZipCode,
        newCity: m.newCity,
        procedure: m.procedure,
        chamber: m.chamber,
        startDate: m.protections.length > 0 ? m.protections[0].startDate:'n/a', //m.protectionStartDate,
        endDate: m.protections.length > 0 ? m.protections[0].endDate: 'n/a', //m.protectionEndDate,
        duration: m.protections.length > 0 ? (m.protections[0]!.duration! > 0?m.protections[0].duration:''): 'n/a', //m.protectionDuration ? String(m.protectionDuration) + m.protectionDurationUom : 'n/a',
        durationUom: m.protections.length > 0 ? m.protections[0]!.durationUom:'', //m.protectionDuration ? String(m.protectionDuration) + m.protectionDurationUom : 'n/a',
        range: m.protections.length > 0 ? (m.protections[0]!.range! > 0?m.protections[0].range:''):'n/a', //m.protectionRange ? String(m.protectionRange) + m.protectionRangeUom : 'n/a'
        rangeUom: m.protections.length > 0 ? m.protections[0]!.rangeUom:'', //m.protectionDuration ? String(m.protectionDuration) + m.protectionDurationUom : 'n/a',
        protectionId: m.protections.length > 0 ? m.protections[0].protectionId: '',
        latitude: m.protections.length > 0 ? m.protections[0].latitude:'',
        longitude: m.protections.length > 0 ? m.protections[0].longitude:'',
        createdBy: m.protections.length > 0 ? m.protections[0].createdBy:'',
        lastModifiedBy: m.protections.length > 0 ? m.protections[0].lastModifiedBy:'',
        type: m.protections.length > 0 ? m.protections[0].type:'',
        status: m.protections.length > 0 ? m.protections[0].status:'',
        pharmacyId: m.protections.length > 0 ? m.protections[0].pharmacyId:'',
        transferRequestId: m.protections.length > 0 ? m.protections[0].transferRequestId:'',
      } as TransferRequestToDisplay;
    });
  };

  onExporting = (e) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');
    exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      customizeCell: function (options) {
        options.excelCell.font = { name: 'Arial', size: 12 };
        options.excelCell.alignment = { horizontal: 'left' };
      },
    }).then(function () {
      workbook.xlsx.writeBuffer().then(function (buffer) {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
      });
    });
  };

  updateProtection = async (data) => {
    this.setState({ isLoading: true });
    // data.startDate = moment(data.dateDeb).format('YYYY-MM-DDTHH:mm:ss');
    // data.endDate = moment(data.dateFin).format('YYYY-MM-DDTHH:mm:ss');
    await this.props.ProtectionStore!.updateProtection(data); //.protectionId, data.lat, data.long);
    await this.props.TransferRequestStore!.loadList();
    await this.props.ProtectionStore!.loadList();
    this.setState({ isLoading: false });
  };

  terminate = async (data) => {
    this.setState({ isLoading: true });
    await this.props.ProtectionStore!.terminate(data);
    await this.props.TransferRequestStore!.loadList();
    await this.props.ProtectionStore!.loadList();
    this.setState({ isLoading: false });
  };

  render() {
    const { isLoading } = this.state;
    const { translate } =this.props;

    return (
      <Container>
        {isLoading ? (
          <Spinner />
        ) : (
        <>

        <DataGrid
          id="registeredRequests"
          dataSource={this.buildGridData()}
          onExporting={this.onExporting}
          showBorders
          allowColumnResizing
          width={'100%'}
          rowAlternationEnabled={true}
          //onRowClick={this.onRowClicked}
        >
          <Export enabled={true} texts={{ exportAll: this.props.translate(TLCode.GRID_EXCEL_EXPORT) }} />
          <Sorting mode="multiple" />
          <FilterPanel visible={true} />
          <HeaderFilter visible={true} />
          <FilterRow visible={true} applyFilter={'auto'} />
          <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
          <Column
            dataType="string"
            dataField="apbNumber"
            caption={this.props.translate(TLCode.PHARMACIES_ApbNum)}
            width={60}
          />
          <Column
            dataType="string"
            dataField="procedure"
            caption={this.props.translate(TLCode.PROTECTION_Type)}
            width={60}
          />
          <Column
            dataType="string"
            dataField="apbNumberToClose"
            caption={this.props.translate(TLCode.PHARMACIES_ApbNum)}
            width={60}
          />
          <Column
            dataType="date"
            dataField="requestDate"
            caption={this.props.translate(TLCode.TRANSFER_REQUEST_requestDate)}
            width={110}
            defaultSortOrder="desc"
            sortIndex={1}
            format="dd/MM/yyyy"
            cellRender={renderGridDate}
          />
          <Column
            dataType="date"
            dataField="decisionDate"
            caption={this.props.translate(TLCode.TRANSFER_REQUEST_decisionDate)}
            width={110}
            format="dd/MM/yyyy"
            cellRender={renderGridDate}
          />
          <Column
            dataType="string"
            dataField="decision"
            caption={this.props.translate(TLCode.TRANSFER_REQUEST_decision)}
            width={80}
          />
          <Column
            dataType="string"
            dataField="newAddress"
            caption={this.props.translate(TLCode.TRANSFER_REQUEST_newAddress)}
            width={200}
          />
          <Column
            dataType="string"
            dataField="newZipCode"
            caption={this.props.translate(TLCode.TRANSFER_REQUEST_newZipCode)}
            width={60}
          />
          <Column
            dataType="string"
            dataField="newCity"
            caption={this.props.translate(TLCode.TRANSFER_REQUEST_newCity)}
            width={100}
          />          
          <Column
            dataType="date"
            dataField="firstPublishDate"
            caption={this.props.translate(TLCode.TRANSFER_REQUEST_firstPublishDate)}
            width={110}
            format="dd/MM/yyyy"
            cellRender={renderGridDate}
          />
          <Column
            dataType="date"
            dataField="firstPublishDateAfterDecision"
            caption={this.props.translate(TLCode.TRANSFER_REQUEST_firstPublishAfterDecisionDate)}
            width={110}
            format="dd/MM/yyyy"
            cellRender={renderGridDate}
          />
          <Column
            dataType="date"
            dataField="startDate"
            caption={this.props.translate(TLCode.PROTECTION_StartDate)}
            width={110}
            format="dd/MM/yyyy"
            cellRender={renderGridDate}
          />
          <Column
            dataType="date"
            dataField="endDate"
            caption={this.props.translate(TLCode.PROTECTION_EndDate)}
            width={110}
            format="dd/MM/yyyy"
            cellRender={renderGridDate}
          />
          <Column
            dataType="number"
            dataField="duration"
            caption={this.props.translate(TLCode.PROTECTION_Duration)}
            width={60}
          />
          <Column
            dataType="string"
            dataField="range"
            caption={this.props.translate(TLCode.PROTECTION_Range)}
            width={60}
          />
          <Column
            dataType="number"
            dataField="latitude"
            caption={this.props.translate(TLCode.PROTECTION_Latitude)}
            width={60}
          />
          <Column
            dataType="number"
            dataField="longitude"
            caption={this.props.translate(TLCode.PROTECTION_Longitude)}
            width={60}
          />
          <Column caption="" type="buttons" width={60}>
            <DGButton
              hint={this.props.LocaleStore!.getText(TLCode.GENERAL_Edit)}
              icon={'edit'}
              onClick={(e) => {
                this.setState({ showProtectionDialog: true, currentTransfer: e.row != undefined?e.row!.data:{} })
              }}
              visible={(e: any) => e.row.data.range > 0}
            />
            <DGButton
              hint={this.props.LocaleStore!.getText(TLCode.GENERAL_Edit)}
              icon={'clear'}
              onClick={async (e) => {
                this.setState({ isLoading: true });
                await this.props.ProtectionStore!.terminate(e.row!.data.protectionId);
                await this.props.TransferRequestStore!.loadList();
                this.setState({ isLoading: false });
              }}
              visible={(e: any) => e.row.data.range > 0}
            />
          </Column>      
          <Pager
            visible={true}
            allowedPageSizes={[20, 50, 100, 'all']}
            showPageSizeSelector={true}
            showInfo={true}
            showNavigationButtons={true}
          />
          <Paging defaultPageSize={20} />
        </DataGrid>
        <ProtectionDialog isOpen={this.state.showProtectionDialog}
          save={this.updateProtection}
          terminate={this.terminate}
          onClose={(e) => {
            this.setState({ showProtectionDialog: false })
          }}
          onCancel={(e) => {
            this.setState({ showProtectionDialog: false })
          }}
          translate={translate}
          transfer={this.state.currentTransfer}
        />
        </>
        )}
      </Container>
    );
  }
}

export default withTranslate(RequestList);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 5%;
  padding-bottom: 5px;
`;
